import {Inject, Injectable} from '@angular/core';
import {ActivitiesAction, FeedGroup} from 'app/common/constants';
import {Observable} from 'rxjs';

export class LogTarget {
    targetFeedKey: FeedGroup;
    targetFeedId: any;
    parentFeeds: any[];
    foreignParams: any;
}
export const MAX_BYTES = 9500; // Maximum size of log entry in bytes 10KB, 500 reserved for other metadata

export interface ILogInfo {
    action?: ActivitiesAction;
    newObj?: any;
    oldObj?: any;
    fields?: any[];
    [key: string]: any;
}

export class LogEntry {
    info: ILogInfo;
    target: LogTarget;

    /**
     * Builds a diff between newObj and oldObj for logging.
     */
    static buildInfo(newObj: any, oldObj: any): any[] {
        let result: any[] = [];
        for (const key in newObj) {
            if (!this.isEmpty(newObj[key]) || !this.isEmpty(oldObj[key])) {
                if (typeof newObj[key] === 'object') {
                    if (Array.isArray(newObj[key])) {
                        oldObj[key] = oldObj[key] ?? [];
                        newObj[key] = newObj[key] ?? [];
                        if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
                            if (typeof newObj[key][0] === 'object') {
                                newObj[key].forEach((obj: any) => {
                                    LogEntry.buildObjectLog(
                                        obj,
                                        key,
                                        oldObj[key].find((i) => i.id === obj.id),
                                        result
                                    );
                                });
                            } else {
                                result.push({ key, value: newObj[key].join(', ') });
                            }
                        }
                    } else {
                        if (!oldObj[key] || JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
                            LogEntry.buildObjectLog(newObj[key], key, oldObj[key], result);
                        }
                    }
                } else if ((!oldObj[key] && newObj[key]) || oldObj[key] !== newObj[key]) {
                    result.push({ key, value: newObj[key] });
                }
            }
        }
        return LogEntry.truncateResultToFitLimit(result, MAX_BYTES);
    }

    static isEmpty(v: any) {
        return v === undefined ||
            v === null ||
            v === '' ||
            (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);;
    }
    /**
     * Builds a diff for nested objects.
     */
    static buildObjectLog(newObj: any, key: string, oldObj: any, result: any[]) {
        let innerResult: any[] = [];
        if(!this.isEmpty(newObj)) {
            if (typeof newObj !== 'object') {
                result.push({ key, value: { ...newObj } });
                return;
            }
            for (const innerkey in newObj) {
                if ((!this.isEmpty(newObj[innerkey]) &&
                    (!oldObj || !oldObj[innerkey])) || (oldObj && oldObj[innerkey] !== newObj[innerkey])) {
                    innerResult.push({ key: innerkey, value: newObj[innerkey] });
                }
            }
        } else if(!this.isEmpty(oldObj)) {
            if (typeof oldObj !== 'object') {
                result.push({ key, value: '-'});
                return;
            }
            for (const innerkey in oldObj) {
                if (!this.isEmpty(oldObj[innerkey])) {
                    innerResult.push({ key: innerkey, value: '-' });
                }
            }
        }
        if (innerResult.length) {
            result.push({ key, value: innerResult });
        }
    }

    /**
     * Truncates the result array to fit within the max byte size.
     */
    static truncateResultToFitLimit(result: any[], maxBytes: number): any[] {
        let encoded = new TextEncoder().encode(JSON.stringify(result));
        if (encoded.length <= maxBytes) return result;

        const itemsWithSize = result.map((item) => ({
            item,
            size: new TextEncoder().encode(JSON.stringify(item)).length,
        }));

        itemsWithSize.sort((a, b) => b.size - a.size);

        let trimmed = [...result];
        for (let i = 0; i < itemsWithSize.length; i++) {
            if (new TextEncoder().encode(JSON.stringify(trimmed)).length <= maxBytes) break;
            const { item } = itemsWithSize[i];
            if (typeof item.value === 'string') {
                item.value = item.value.substring(0, 50) + '...';
            } else if (Array.isArray(item.value)) {
                item.value = `[${item.value.length} ...]`;
            } else if (typeof item.value === 'object') {
                item.value = '[...]';
            } else {
                item.value = '[...]';
            }
        }
        while (new TextEncoder().encode(JSON.stringify(trimmed)).length > maxBytes && trimmed.length > 1) {
            trimmed.pop();
        }
        return trimmed;
    }
}

export abstract class ActivityTrackerPublisher {
    abstract log(record: LogEntry): Observable<boolean>;
    abstract logAsObservable(record: LogEntry): Observable<Promise<any>>;
}

@Injectable({
    providedIn: 'root',
})
export class ActivityTrackerService {
    publisher: ActivityTrackerPublisher;
    constructor(
        @Inject(ActivityTrackerPublisher)
        private publisherService: ActivityTrackerPublisher
    ) {
        this.publisher = this.publisherService;
    }

    log(info: any, target: LogTarget): void {
        let entry = this.createLogEntry(info, target);

        if (entry) this.writeToLog(entry);
    }

    logAsObservable(info: any, target: LogTarget): Observable<Promise<any>> {
        let entry = this.createLogEntry(info, target);
        return this.publisher.logAsObservable(entry);
    }

    createLogEntry(info: any, target: LogTarget): LogEntry {
        let entry: LogEntry = new LogEntry();
        entry.target = target;
        entry.info = {};
        for (const key in info) {
            if (info[key]) {
                if (key === 'newObj') {
                    entry.info['fields'] = LogEntry.buildInfo(
                        info.newObj,
                        info.oldObj || {}
                    );
                } else if (key !== 'oldObj') {
                    entry.info[key] = info[key];
                }
            }
        }
        if (
            [ActivitiesAction.update, ActivitiesAction.update_bulk].indexOf(
                entry.info.action
            ) >= 0 &&
            !entry.info.fields?.length &&
            !entry.info.count
        ) {
            return;
        }
        return entry;
    }

    private writeToLog(entry: LogEntry) {
        this.publisher.log(entry).subscribe({});
    }
}
